import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, FlatList } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFontSize, FontSizeName, Theme, useThemedStyles, useTheme } from '../theme';
import { useTranslation } from 'react-i18next';

interface Props {
  onBack: () => void;
}

const FontSizeSettings: React.FC<Props> = ({ onBack }) => {
  const { fontSizeName, setFontSizeName } = useFontSize();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const OPTIONS: { key: FontSizeName; title: string; description: string }[] = [
    { key: 'small', title: t('settings.small'), description: t('settings.smallDesc') },
    { key: 'default', title: t('settings.default'), description: t('settings.defaultDesc') },
    { key: 'large', title: t('settings.large'), description: t('settings.largeDesc') },
  ];

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}> 
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.headerIcon}>
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('settings.fontSize')}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <FlatList
        data={OPTIONS}
        keyExtractor={(item) => item.key}
        renderItem={({ item }) => {
          const selected = item.key === fontSizeName;
          return (
            <TouchableOpacity
              onPress={() => setFontSizeName(item.key)}
              style={styles.item}
              activeOpacity={0.7}
            >
              <View style={styles.itemTextContainer}>
                <Text style={styles.itemTitle}>{item.title}</Text>
                <Text style={styles.itemDescription}>{item.description}</Text>
              </View>
              <View style={[styles.radio, selected && styles.radioSelected]}>
                {selected && <Ionicons name="checkmark" size={16} color={theme.colors.primaryText} />}
              </View>
            </TouchableOpacity>
          );
        }}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        contentContainerStyle={styles.listContent}
      />
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  headerIcon: {
    padding: 8,
  },
  headerSpacer: {
    width: 24,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  listContent: {
    backgroundColor: theme.colors.card,
    marginTop: 16,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
  },
  item: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  itemTextContainer: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: '600',
  },
  itemDescription: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 4,
  },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.card,
  },
  radioSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary,
  },
  separator: {
    height: 0.5,
    backgroundColor: theme.colors.border,
    marginLeft: 16,
  },
});

export default FontSizeSettings;
